import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Paper,
  Popper,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';

import { resumeFileUrl } from '@constants';
import { CandidatesWithPosition } from '@types';
import { formatVideoDuration, useVideoThumbnail } from '@hooks';
import { NotProvided } from 'components/common/NotProvided/NotProvided';
import { cardButtonSx } from 'theme/buttons';

const TILE = { width: '3.5rem', height: '2rem' };
const PREVIEW_WIDTH = '18rem';
const OPEN_DELAY_MS = 400;
const CLOSE_GRACE_MS = 180;

const EmptyTile: React.FC<{ title: string }> = ({ title }) => (
  <Tooltip title={title} placement="top">
    <Box
      component="span"
      sx={(theme) => ({
        ...TILE,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        borderRadius: 1,
        backgroundColor: theme.palette.highlight.neutral,
        color: theme.palette.text.lightGrey,
        fontSize: '0.75rem',
      })}
    >
      n/a
    </Box>
  </Tooltip>
);

const VideoIntroThumb: React.FC<{
  candidate: CandidatesWithPosition;
  onPlay: () => void;
}> = ({ candidate, onPlay }) => {
  const { ref, url } = useVideoThumbnail(candidate.videoFileName);
  const [duration, setDuration] = useState<number | null>(null);
  const [frameFailed, setFrameFailed] = useState(false);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>();

  const isTouch = useMediaQuery('(hover: none)');
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

  const hasFrame = Boolean(url) && !frameFailed;

  const openLater = useCallback(
    (event: React.SyntheticEvent<HTMLElement>) => {
      if (isTouch || !url) return;
      const target = event.currentTarget;
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setAnchor(target), OPEN_DELAY_MS);
    },
    [isTouch, url],
  );

  const closeSoon = useCallback(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setAnchor(null), CLOSE_GRACE_MS);
  }, []);

  useEffect(() => () => clearTimeout(timer.current), []);

  if (candidate.videoFileName === undefined)
    return <NotProvided field="video intros" />;

  if (!candidate.videoFileName)
    return <EmptyTile title="No video intro on file" />;

  const badge = duration ? formatVideoDuration(duration) : '';

  return (
    <>
      <Tooltip
        title={
          duration
            ? `Play the video intro · ${formatVideoDuration(duration)}`
            : 'Play the video intro'
        }
        placement="top"
        disableHoverListener={Boolean(anchor)}
      >
        <Box
          ref={ref}
          component="button"
          type="button"
          aria-label={`Play ${candidate.firstName} ${candidate.lastName}'s video intro`}
          onClick={(event: React.MouseEvent) => {
            event.stopPropagation();
            onPlay();
          }}
          onPointerEnter={openLater}
          onPointerLeave={closeSoon}
          onFocus={openLater}
          onBlur={closeSoon}
          sx={(theme) => ({
            ...TILE,
            position: 'relative',
            flexShrink: 0,
            padding: 0,
            overflow: 'hidden',
            cursor: 'pointer',
            borderRadius: 1,
            border: `1px solid ${theme.palette.background.divider}`,
            backgroundColor: theme.palette.text.linkLight,
            display: 'inline-grid',
            placeItems: 'center',
            transition: theme.transitions.create('transform'),
            '&:hover': { transform: 'scale(1.05)' },
          })}
        >
          {url && !frameFailed && (
            <Box
              component="video"
              src={`${url}#t=1`}
              muted
              playsInline
              preload="metadata"
              onLoadedMetadata={(
                event: React.SyntheticEvent<HTMLVideoElement>,
              ) => setDuration(event.currentTarget.duration)}
              onError={() => setFrameFailed(true)}
              sx={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                pointerEvents: 'none',
              }}
            />
          )}
          <Box
            component="span"
            sx={(theme) => ({
              position: 'absolute',
              inset: 0,
              display: 'grid',
              placeItems: 'center',
              color: theme.palette.common.white,
              backgroundColor: hasFrame ? 'rgba(0, 0, 0, 0.35)' : 'transparent',
              transition: theme.transitions.create('background-color'),
              ...(hasFrame ? {} : { color: theme.palette.text.link }),
            })}
          >
            <PlayArrowIcon sx={{ fontSize: '1.25rem' }} />
          </Box>
          {Boolean(badge) && (
            <Box
              component="span"
              sx={(theme) => ({
                position: 'absolute',
                right: 2,
                bottom: 2,
                px: '3px',
                borderRadius: '3px',
                backgroundColor: theme.palette.text.primary,
                color: theme.palette.text.invertedPrimary,
                fontSize: '0.5625rem',
                lineHeight: 1.44,
                fontWeight: 500,
                fontVariantNumeric: 'tabular-nums',
              })}
            >
              {badge}
            </Box>
          )}
        </Box>
      </Tooltip>

      <Popper
        open={Boolean(anchor) && Boolean(url)}
        anchorEl={anchor}
        placement="right-start"
        modifiers={[{ name: 'offset', options: { offset: [0, 8] } }]}
        sx={{ zIndex: (theme) => theme.zIndex.tooltip }}
      >
        <Paper
          elevation={8}
          onPointerEnter={() => clearTimeout(timer.current)}
          onPointerLeave={closeSoon}
          onClick={(event) => event.stopPropagation()}
          sx={{ width: PREVIEW_WIDTH, overflow: 'hidden', borderRadius: 1 }}
        >
          <Box
            component="video"
            src={`${url}#t=1`}
            muted
            loop
            playsInline
            autoPlay={!reducedMotion}
            controls={reducedMotion}
            sx={{ display: 'block', width: '100%', backgroundColor: 'black' }}
          />
          <Box px={1.5} py={1}>
            <Typography variant="body2" fontWeight={500} noWrap>
              {`${candidate.firstName} ${candidate.lastName}`}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Video intro{duration ? ` · ${formatVideoDuration(duration)}` : ''}
            </Typography>
          </Box>
        </Paper>
      </Popper>
    </>
  );
};

export const ResumeLink: React.FC<{
  candidate: CandidatesWithPosition;
  label?: string;
}> = ({ candidate, label }) => {
  if (candidate.resumeName === undefined)
    return <NotProvided field="resumes" />;

  if (!candidate.resumeName)
    return (
      <Tooltip title="No resume on file" placement="top">
        <Box
          component="span"
          sx={(theme) => ({
            display: 'grid',
            placeItems: 'center',
            width: '1.75rem',
            height: '1.75rem',
            flexShrink: 0,
            color: theme.palette.background.dividerStrong,
          })}
        >
          <DescriptionOutlinedIcon sx={{ fontSize: '1.25rem' }} />
        </Box>
      </Tooltip>
    );

  const linkProps = {
    component: 'a',
    href: resumeFileUrl(candidate.resumeName),
    target: '_blank',
    rel: 'noopener',
    'aria-label': `Open ${candidate.firstName} ${candidate.lastName}'s full resume`,
    onClick: (event: React.MouseEvent) => event.stopPropagation(),
  } as const;

  if (label)
    return (
      <Button
        size="small"
        startIcon={<DescriptionOutlinedIcon />}
        {...linkProps}
        sx={{
          ...cardButtonSx,
          px: 0.75,
          color: 'text.link',
          '@media (hover: hover)': {
            '&:hover': { backgroundColor: 'text.linkLight' },
          },
        }}
      >
        {label}
      </Button>
    );

  return (
    <Tooltip title="Open full resume" placement="top">
      <IconButton
        size="small"
        {...linkProps}
        sx={{
          width: '1.75rem',
          height: '1.75rem',
          color: 'text.link',
          flexShrink: 0,
          '&:hover': { backgroundColor: 'text.linkLight' },
        }}
      >
        <DescriptionOutlinedIcon sx={{ fontSize: '1.25rem' }} />
      </IconButton>
    </Tooltip>
  );
};

export const CandidateMaterials: React.FC<{
  candidate: CandidatesWithPosition;
  showVideo?: boolean;
  onPlay: () => void;
}> = ({ candidate, showVideo = true, onPlay }) => (
  <Box display="flex" alignItems="center" gap={1}>
    {showVideo && <VideoIntroThumb candidate={candidate} onPlay={onPlay} />}
    <ResumeLink candidate={candidate} />
  </Box>
);
